import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, filter, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { ConfigService } from '@bo-client/config/config.service';

/**
 * IBAN-Eingabe mit Online-Prüfung über `ibanCheckApi`; übernimmt bei gültiger IBAN BIC und Bankname.
 *
 * Ob die IBAN Pflicht ist, legt der Aufrufer über `Validators.required` am Control `iban` fest.
 * Ein vorbefüllter Wert wird beim Laden einmal geprüft, jede Änderung erneut — gespeicherte Werte
 * können ungeprüft sein, weil „Speichern" ohne Formularprüfung läuft.
 */
@Component({
  selector: 'app-get-iban',
  templateUrl: './get-iban.component.html',
  styleUrls: ['./get-iban.component.scss']
})
export class GetIbanComponent implements OnInit, OnDestroy {
  @Input() inputForm: FormGroup;
  public dataForm$: Observable<any>;
  private compUnsubs$ = new Subject<void>();
  public bankName = null;
  public ibanIsRequired = false;
  /** Zuletzt von `ibanCheckApi` als gültig bestätigte IBAN, normalisiert (ohne Leerzeichen, Großbuchstaben). */
  private confirmedIban: string | null = null;

  constructor(private httpHapService: HttpHapService, public configService: ConfigService) {}

  ngOnInit(): void {
    this.ibanIsRequired = this.iban.hasValidator(Validators.required);
    this.setValidators(this.ibanIsRequired);
    this.changeFormValues();
  }

  changeFormValues() {
    this.dataForm$ = this.iban.valueChanges.pipe(
      startWith(this.iban.value),
      takeUntil(this.compUnsubs$),
      filter((val: string) => GetIbanComponent.normalize(val).length > 1),
      switchMap((val: string) =>
        this.httpHapService.httpClientback
          .post(this.configService.getSettings('ibanCheckApi'), { iban: GetIbanComponent.normalize(val) })
          .pipe(
            tap((answer: any) => {
              if (answer.payload.status.toLowerCase() === 'valid') {
                this.confirmedIban = GetIbanComponent.normalize(answer.payload.ibanReadable ?? val);
                this.iban.patchValue(answer.payload.ibanReadable, { emitEvent: false });
                this.iban.updateValueAndValidity({ emitEvent: false });
                this.inputForm.get('bic').patchValue(answer.payload.bic);
                this.inputForm.get('bankName').patchValue(answer.payload.bankName);
              } else {
                this.inputForm.get('bic').patchValue(null);
                this.inputForm.get('bankName').patchValue(null);
                this.iban.markAsTouched();
              }
            }),
            // Ohne Antwort bleibt der Wert unbestätigt (ungültig); spätere Eingaben werden weiter geprüft.
            catchError(() => EMPTY)
          )
      )
    );
  }

  pasteValue(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData.getData('text');
    setTimeout(() => {
      this.iban.patchValue(pastedText);
    }, 500);
  }

  setValidators(isRequired: boolean) {
    this.iban.setValidators(
      isRequired ? [Validators.required, this.isValidIbanValidator()] : [this.isValidIbanValidator()]
    );
  }

  /**
   * Leerer Wert: gültig (die Pflicht regelt `Validators.required`). Sonst gültig nur, wenn der Wert der
   * zuletzt von `ibanCheckApi` bestätigten IBAN entspricht; bis zur Antwort gilt `{ iban: true }`.
   */
  private isValidIbanValidator(): ValidatorFn {
    return (c: AbstractControl): ValidationErrors | null => {
      const value = GetIbanComponent.normalize(c.value);
      if (!value || value === this.confirmedIban) {
        return null;
      }
      return { iban: true };
    };
  }

  get errorText(): string {
    return this.iban.hasError('required') ? 'IBAN ist erforderlich' : 'IBAN ist nicht gültig';
  }

  get iban() {
    return this.inputForm.get('iban') as FormControl;
  }

  private static normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/ /g, '').toUpperCase();
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }
}
